/**
 * Taking a publication down, as a decision of its own: never part of opening
 * a change to the programme, which leaves the publication untouched.
 *
 * @module components/portfolio/WithdrawPublicationDialog
 */

import React from "react";

import { Alert, Button, FormField, Modal, TextField } from "@/components/ui";

export interface WithdrawPublicationDialogProps {
  open: boolean;
  /** The edition being withdrawn, named so the decision says what it acts on. */
  edition: number;
  reason: string;
  onReasonChange: (reason: string) => void;
  busy: boolean;
  error: string | null;
  onWithdraw: () => void;
  onClose: () => void;
}

/**
 * @example
 * ```tsx
 * <WithdrawPublicationDialog open={open} edition={1} reason={reason} onReasonChange={setReason}
 *   busy={false} error={null} onWithdraw={withdraw} onClose={close} />
 * ```
 */
export function WithdrawPublicationDialog({
  open,
  edition,
  reason,
  onReasonChange,
  busy,
  error,
  onWithdraw,
  onClose,
}: WithdrawPublicationDialogProps): React.ReactElement {
  const refusal = reason.trim() ? undefined : "Give a reason for the withdrawal";
  return (
    <Modal
      open={open}
      onClose={onClose}
      tone="danger"
      // The body is a form, not the consequence; the subtitle carries that.
      describe={false}
      title="Withdraw this publication?"
      subtitle={`Edition ${edition}. The public curriculum API stops serving it.`}
      closeRefusal={busy ? "The withdrawal is being recorded" : undefined}
      data-testid="portfolio-withdraw-confirmation"
      footer={
        <>
          <Button
            variant="ghost"
            icon="x"
            disabled={busy}
            disabledReason="Wait for the withdrawal to finish."
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            tone="danger"
            icon="cloud-slash"
            loading={busy}
            loadingLabel="Withdrawing"
            disabled={Boolean(refusal)}
            disabledReason={refusal}
            onClick={onWithdraw}
            data-testid="portfolio-withdraw-submit"
          >
            Withdraw publication
          </Button>
        </>
      }
    >
      <FormField
        label="Withdrawal reason"
        htmlFor="portfolio-withdrawal-reason"
        helpText="Saved with the withdrawal."
        required
      >
        <TextField
          id="portfolio-withdrawal-reason"
          value={reason}
          onChange={onReasonChange}
          placeholder="e.g. Superseded by revision 2.0"
          required
          disabled={busy}
          disabledReason="Wait for the withdrawal to finish."
          data-testid="portfolio-withdrawal-reason"
        />
      </FormField>

      {/* The dialog covers the panel's own alert, so a refused withdrawal has
          nowhere else to report itself. */}
      {error ? (
        <Alert tone="warning" className="mt-4" data-testid="portfolio-withdraw-error">
          {error}
        </Alert>
      ) : null}
    </Modal>
  );
}
